import React, {CSSProperties, ReactElement} from "react";
import {connect} from "react-redux";
import {selectDialog} from "@redux/selectors/dialog";
import {IDialog, update as updateDialog} from "@redux/slices/dialog";
import {Update} from "@redux/slices/base";
import {COLOURS, THEME} from "@components/core/theme";

// ── Border geometry ────────────────────────────────────────────────────────────
const OUTER = 3;   // px inset to outer line
const BAND  = 12;  // band width (how thick the border is)
const CELL  = 32;  // length of each alternating box along the edge
const INNER = OUTER + BAND;

// Preview box dimensions
const PW = 320, PH = 180;

// Generate sequential alternating rectangles around the perimeter, centred so
// the clipped portion is equal at both ends of each edge.
// Vertical edges start with the same colour as the first horizontal box so the
// corners appear visually consistent.
function borderTiles(pw: number, ph: number, dark: string, light: string): ReactElement[] {
    const tiles: ReactElement[] = [];
    let k = 0;
    const c = (i: number, startDark: boolean) => (i % 2 === 0) === startDark ? dark : light;

    // Centred start points — force odd count so first and last partial share the same colour
    let hN = Math.ceil((pw - OUTER * 2) / CELL);
    if (hN % 2 === 0) hN++;
    const hStart = OUTER - (hN * CELL - (pw - OUTER * 2)) / 2;

    let vN = Math.ceil((ph - (OUTER + BAND) * 2) / CELL);
    if (vN % 2 === 0) vN++;
    const vStart = (OUTER + BAND) - (vN * CELL - (ph - (OUTER + BAND) * 2)) / 2;

    // Horizontal boxes always start dark (index 0)
    for (let i = 0; i < hN; i++) {
        const x1 = Math.max(hStart + i * CELL, OUTER);
        const x2 = Math.min(hStart + (i + 1) * CELL, pw - OUTER);
        tiles.push(<rect key={k++} x={x1} y={OUTER} width={x2 - x1} height={BAND} fill={c(i, true)}/>);
        tiles.push(<rect key={k++} x={x1} y={ph - OUTER - BAND} width={x2 - x1} height={BAND} fill={c(i, true)}/>);
    }

    // Vertical boxes: match the colour that appears at the top-left corner (index 0 of horizontal = dark)
    for (let i = 0; i < vN; i++) {
        const y1 = Math.max(vStart + i * CELL, OUTER + BAND);
        const y2 = Math.min(vStart + (i + 1) * CELL, ph - OUTER - BAND);
        tiles.push(<rect key={k++} x={OUTER} y={y1} width={BAND} height={y2 - y1} fill={c(i, true)}/>);
        tiles.push(<rect key={k++} x={pw - OUTER - BAND} y={y1} width={BAND} height={y2 - y1} fill={c(i, true)}/>);
    }

    return tiles;
}

interface IVariant { id: string; dark: string; light: string; line: string }

function BorderSvg({id, dark, light, line}: IVariant) {
    const gridId = `grid-${id}`;
    return (
        <svg width={PW} height={PH} style={{display: "block"}}>
            <rect width={PW} height={PH} fill="#4a6741"/>
            <defs>
                <pattern id={gridId} width="20" height="20" patternUnits="userSpaceOnUse">
                    <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1"/>
                </pattern>
            </defs>
            <rect width={PW} height={PH} fill={`url(#${gridId})`}/>
            {/* sequential alternating tiles around perimeter */}
            {borderTiles(PW, PH, dark, light)}
            {/* outer solid line */}
            <rect x={OUTER} y={OUTER} width={PW - OUTER * 2} height={PH - OUTER * 2}
                  fill="none" stroke={line} strokeWidth="2"/>
            {/* inner solid line */}
            <rect x={INNER} y={INNER} width={PW - INNER * 2} height={PH - INNER * 2}
                  fill="none" stroke={line} strokeWidth="2"/>
            {/* second inner line */}
            <rect x={INNER + 4} y={INNER + 4} width={PW - (INNER + 4) * 2} height={PH - (INNER + 4) * 2}
                  fill="none" stroke={line} strokeWidth="1"/>
        </svg>
    );
}

const styles: Record<string, CSSProperties> = {
    overlay: {
        alignItems: "center",
        backgroundColor: COLOURS.overlay,
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        left: 0,
        position: "absolute",
        right: 0,
        top: 0,
        zIndex: 200
    },
    dialog: {
        backgroundColor: COLOURS.parchment,
        border: `2px solid ${COLOURS.agedBorder}`,
        borderRadius: 8,
        boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        margin: 16,
        maxWidth: 400,
        padding: 20,
        width: "calc(100% - 32px)"
    },
    title: {
        color: COLOURS.forestGreen,
        fontFamily: "Georgia, 'Palatino Linotype', serif",
        fontSize: "large",
        textAlign: "center"
    },
    card: {
        display: "flex",
        flexDirection: "column",
        gap: 6
    },
    cardTitle: {
        color: COLOURS.forestGreen,
        fontFamily: "Georgia, 'Palatino Linotype', serif",
        fontWeight: "bold",
        fontSize: "small"
    },
    cardDesc: {
        color: COLOURS.ink,
        fontFamily: "Georgia, 'Palatino Linotype', serif",
        fontSize: "small",
        marginBottom: 4
    },
    row: {
        display: "flex",
        justifyContent: "flex-end"
    }
};

interface IBorderProposalDialog {
    dialog: IDialog
    updateDialog: Update
}

function BorderProposalDialog({dialog, updateDialog}: IBorderProposalDialog) {
    if (dialog.name !== "borderProposal") return null;

    const choose = (style: string) => {
        localStorage.setItem("borderStyle", style);
        updateDialog({update: {}});
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.dialog}>
                <div style={styles.title}>Map Border Proposals</div>

                <div style={styles.card}>
                    <div style={styles.cardTitle}>A — Classic</div>
                    <BorderSvg id="a" dark="#000000" light="#ffffff" line="#000000"/>
                    <div style={styles.cardDesc}>Black and white with black lines — traditional Ordnance Survey.</div>
                    <div style={styles.row}>
                        <button onClick={() => choose("classic")} style={THEME.button}>Choose</button>
                    </div>
                </div>

                <div style={styles.card}>
                    <div style={styles.cardTitle}>B — Dark Wood &amp; Parchment</div>
                    <BorderSvg id="b" dark={COLOURS.darkWood} light={COLOURS.parchment} line={COLOURS.darkWood}/>
                    <div style={styles.cardDesc}>Warm dark brown and parchment — earthy, aged map feel.</div>
                    <div style={styles.row}>
                        <button onClick={() => choose("darkWood")} style={THEME.button}>Choose</button>
                    </div>
                </div>

                <div style={styles.card}>
                    <div style={styles.cardTitle}>C — Forest &amp; Parchment</div>
                    <BorderSvg id="c" dark={COLOURS.forestGreen} light={COLOURS.parchmentDark}
                               line={COLOURS.forestGreen}/>
                    <div style={styles.cardDesc}>Forest green and parchment — matches the game's colour scheme.</div>
                    <div style={styles.row}>
                        <button onClick={() => choose("forestParchment")} style={THEME.button}>Choose</button>
                    </div>
                </div>

            </div>
        </div>
    );
}

const mapStateToProps = (store: never) => ({
    dialog: selectDialog(store)
});
const connected = connect(mapStateToProps, {
    updateDialog
})(BorderProposalDialog);
export {connected as BorderProposalDialog}
